<template>
  <div
    class="main-product critical-component-clear"
    :class="{ [$class('loading')]: state === 'loading' }"
  >
    <div
      id="product-gallery"
      class="main-product__gallery-container"
      data-sign-post-location="main"
      js-sign-post
    >
      <product-gallery
        class="main-product__gallery"
        :badges="badges"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :loading="state === 'loading'"
        :product="product || liquid"
        :variant="variant"
        @update-current-index="handleUpdateCurrentIndex"
        @update-current-namespace="handleUpdateCurrentNamespace"
      />
    </div>

    <div class="main-product__meta-container">
      <div class="main-product__meta-overflow">
        <div class="main-product__meta">
          <div class="main-product__details">
            <h1
              id="template-title"
              class="
                main-product__title
                text-heading-l
                text-heading-xl-desktop
              "
              v-text="product.title || liquid.title"
            />

            <product-prices
              v-if="!disablePurchase"
              class="main-product__price"
              :available="product.available"
              class-compare-at-price="main-product__compare-at-price-text"
              class-price="main-product__price-text"
              :loading="state === 'loading'"
              :price="product?.price?.amount"
              :compare-at-price="product?.compareAtPrice?.amount"
              :varies="false"
            />

            <span
              v-if="currentVariantSku"
              class="main-product__sku text-body-s-regular"
              v-text="currentVariantSku"
            />
          </div>

          <product-form
            class="main-product__form"
            :disable-purchase="disablePurchase"
            :is-variant-selected="Boolean(variant)"
            :linked-products="linkedProducts"
            :loading="state === 'loading'"
            :product-add-on="productAddOn"
            :product="product || liquid"
            :section="section"
            size-guide
            :size-guide-measuring-guide="sizeGuideMeasuringGuide"
            :size-guide-video="sizeGuideVideo"
            template="product"
            :variant="variant"
            @update-variant="handleVariantUpdate"
          />

          <div
            v-if="
              ($variable('settings.productQuickLinks.enableFreeReturnGuarantee') ||
                discussForumLink || sizingLink) &&
                !disablePurchase
            "
            class="main-product__quick-links-wrapper"
          >
            <component
              :is="$variable('settings.productQuickLinks.freeReturnGuaranteeUrl') ? 'a' : 'div'"
              v-if="$variable('settings.productQuickLinks.enableFreeReturnGuarantee')"
              class="main-product__quick-link"
              :href="$variable('settings.productQuickLinks.freeReturnGuaranteeUrl')"
              :target="
                $variable('settings.productQuickLinks.freeReturnGuaranteeNewTab')
                  ? '_blank' : null
              "
            >
              <icon-return />

              <span
                class="text-body-s-regular"
                v-text="$string('product.quick_links.free_return_guarantee')"
              />

              <icon-plus />
            </component>

            <a
              v-if="discussForumLink"
              class="main-product__quick-link"
              :href="discussForumLink"
              :target="$variable('settings.productQuickLinks.forumNewTab') ? '_blank' : null"
            >
              <icon-forum />

              <span
                class="text-body-s-regular"
                v-text="$string('product.quick_links.discuss_in_forum')"
              />

              <icon-plus />
            </a>

            <a
              v-if="sizingLink.enable && sizingLink.link"
              class="main-product__quick-link"
              :href="sizingLink.link"
              :target="sizingLink.newTab ? '_blank' : null"
            >
              <!-- eslint-disable-next-line -->
              <img
                v-if="sizingLink.icon"
                class="main-product__quick-link-icon"
                alt=""
                height="24"
                :src="sizingLink.icon"
                role="presentation"
                width="24"
              >

              <icon-forum v-else />

              <span
                class="text-body-s-regular"
                v-text="sizingLink.text"
              />

              <icon-plus />
            </a>
          </div>

          <div
            v-if="productDescription"
            class="main-product__description"
            :class="{ [$class('active')]: showFullDescription }"
          >
            <button
              v-if="truncatedProductDescription !== productDescription"
              class="main-product__description-partial"
              type="button"
              @click="handleDescriptionClick"
            >
              <rte-content
                :content="truncatedProductDescription"
                aria-hidden="true"
              />
            </button>

            <rte-content
              :class="{
                'main-product__description-full': truncatedProductDescription !== productDescription
              }"
              :content="productDescription"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main product (main-product)
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {Object} [badges] - Product badges.
 * @param {Boolean} [disablePurchase] - Disable add to cart and price display.
 * @param {String} descriptionStripped - Product description with the HTML
 * removed, used to determine if description is empty.
 * @param {Array<Object>} [linkedProducts] - Colour swatches.
 * @param {Object} liquid - Formatted product Liquid object.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Boolean} [productAddOn] - If product is an add-on.
 * @param {Number} [readMoreWordCount] - Read more word count.
 * @param {Object} section - Section settings.
 * @param {Boolean|Object} [sizeGuideMeasuringGuide] - Size guide measuring
 * guide metaobject data.
 * @param {Boolean|Object} [sizeGuideVideo] - Size guide video Metaobject data.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 * @emits updateRequestObject - Emit new product object.
 *
 */
import ProductForm from '~async/product-form/product-form'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import RteContent from '~async/rte-content/rte-content'

import IconForum from '~icons/general/forum.svg'
import IconReturn from '~icons/general/return.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'

import { emitUpdateRequestObject } from '~/helpers/vue'

export default {
  name: 'MainProduct',

  components: {
    IconForum,
    IconPlus,
    IconReturn,
    ProductForm,
    ProductGallery,
    ProductPrices,
    RteContent,
  },

  props: {
    badges: {
      type: Object,
      default: () => ({}),
    },
    disablePurchase: {
      type: Boolean,
      default: false,
    },
    descriptionStripped: {
      type: String,
      default: '',
    },
    linkedProducts: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
      }),
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    productAddOn: {
      type: Boolean,
      default: false,
    },
    readMoreWordCount: {
      type: Number,
      default: 30,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        optionStyle: '',
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        hideInvalidValues: false,
        id: '',
        initialSelect: true,
        resetFormBehaviour: 'disabled',
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
        showPricingText: true,
      }),
    },
    sizeGuideMeasuringGuide: {
      type: [Boolean, Object],
      default: false,
    },
    sizeGuideVideo: {
      type: [Boolean, Object],
      default: false,
    },
    sizingLink: {
      type: Object,
      default: () => ({
        enable: true,
        icon: '',
        link: '',
        newTab: true,
        text: '',
      }),
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  emits: {
    'update-request-object': emitUpdateRequestObject,
  },

  data() {
    return {
      currentIndex: 0,
      currentNamespace: 'gallery',
      handle: this.$variable('page.handle'),
      showFullDescription: false,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Compute the current variant sku.
     * - Uses the selected or first available variants sku if it exists.
     * - Otherwise returns false and will hide the text.
     * @returns {String|Boolean}
     */
    currentVariantSku() {
      if (!this.product || !this.product?.selectedOrFirstAvailableVariant) {
        return false
      }

      let currentSku = this.product?.selectedOrFirstAvailableVariant?.sku || ''
      currentSku = currentSku.split('/')[0]

      return currentSku || false
    },

    /**
     * Compute discuss forum link.
     * @returns {String}
     */
    discussForumLink() {
      const discussForumEnabled = this.$variable('settings.productQuickLinks.enableDiscussInForumLink')

      if (!discussForumEnabled || !this.product) {
        return ''
      }

      const url = this.product?.forumLink?.value

      return url || ''
    },

    /**
     * Compute product description to remove size guide tables.
     * @returns {String}
     */
    productDescription() {
      const matches = this.descriptionStripped.match(/[\w\d]/g)

      if (!this.descriptionStripped || !matches?.length) {
        return ''
      }

      let description = this.product.description || this.liquid.description

      if (!description) {
        return ''
      }

      description = description
        .replaceAll('<meta charset="utf-8">', '')
        .replaceAll('<meta charset="utf-8" />', '')
        .replaceAll('<span>', '')
        .replaceAll('</span>', '')
        .replaceAll('<p></p>', '')
        .replaceAll('<p>&nbsp;</p>', '')
        .replaceAll('<p> </p>', '')

      if (description.includes('[SIZE-GUIDE')) {
        description = description.split('[SIZE-GUIDE')[0]
      }

      return description.trim()
    },

    /**
     * Computes truncated product description.
     * @returns {String}
     */
    truncatedProductDescription() {
      const words = this.productDescription.split(/\s/g)

      if (!words || words?.length <= this.readMoreWordCount) {
        return this.productDescription
      }

      const truncated = words
        .slice(0, this.readMoreWordCount)
        .join(' ')

      const readMore = `
        <span class="text-body-s-underline">
          ${this.$string('product.read_more')}
        </span>
      `

      return `${truncated}... ${readMore}`
    },
  },

  mounted() {
    this.setProduct()
  },

  methods: {

    /**
     * Set Liquid product object to product.
     */
    setProduct() {
      this.$emit('update-request-object', {
        response: this.liquid,
        type: 'product',
      })

      this.state = 'ready'
    },

    /**
     * Update current carousel index.
     * @param {Number} newIndex - New carousel index.
     */
    handleUpdateCurrentIndex(newIndex) {
      if (newIndex === this.currentIndex) {
        return
      }

      this.currentIndex = newIndex
    },

    /**
     * Update current carousel namespace.
     * @param {String} newNamespace - New carousel namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.currentNamespace = newNamespace
    },

    /**
     * Handle variant update from product form.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      const params = new URLSearchParams(document.location.search.substring(1))

      if (variant?.id) {
        params.set('variant', variant.id)
      }

      const url = variant?.id
        ? `${location.origin}${location.pathname}?${params.toString()}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Handle description click.
     */
    handleDescriptionClick() {
      this.showFullDescription = true
    },
  },
}
</script>

<style lang="scss">
@import '~async/product-gallery-modal/product-gallery-modal-overlay';
@import './main-product';
</style>
